import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getTheme } from '../theme';
import { useLayoutPreferences } from './LayoutPreferencesContext';

interface ThemeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  darkMode: false,
  toggleDarkMode: () => {},
});

export const useThemeMode = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const stored = localStorage.getItem('kalpa-dark-mode');
    return stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem('kalpa-dark-mode', String(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode((prev) => !prev);

  // Text size lives in LayoutPreferencesContext with the other Display &
  // Layout axes, but only the theme can apply it, so it is read back here.
  // That context's default is non-throwing, so a ThemeProvider mounted without
  // it (as several tests do) simply renders at 'normal'.
  const { textSize } = useLayoutPreferences();

  const theme = useMemo(() => getTheme(darkMode, textSize), [darkMode, textSize]);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
